import { Flex, Heading, Skeleton } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { EmptyState } from "src/components/EmptyState";
import { GenericError } from "src/components/Feedback/GenericError";
import { ParentFormsLayoutInner } from "src/components/Layout/ParentFormsLayoutInner";
import { ParentFormsLayoutOuter } from "src/components/Layout/ParentFormsLayoutOuter";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataRestQuery } from "src/hooks/useRemoteDataQuery";
import EmptyWalletSvg from "src/images/empty-wallet.svg?react";
import { isNonEmptyString, isNullOrUndefined } from "src/services/predicates";
import * as Url from "src/services/url";
import { PaidPaymentCard, PendingPaymentCard } from "./PaymentCard";

export const List = () => {
  const organization = useOrganization();
  const [selectedPaymentId, setSelectedPaymentId] = React.useState<
    null | string
  >(null);
  const avelaToast = useAvelaToast({ position: "bottom" });

  const queryKey = React.useMemo(
    () => ["GetPaymentsForMe", organization.toNullable()?.id],
    [organization]
  );
  const remoteData = useRemoteDataRestQuery({
    enabled: organization.hasData(),
    queryKey: queryKey,
    queryFn: () =>
      FSR.getPaymentsForMe({
        query: { organizationId: organization.toNullable()?.id ?? "" },
      }).then((resp) => resp.data),
  });

  const queryClient = useQueryClient();
  const refetch = React.useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  const checkout = React.useCallback(
    async (payment: FSR.PaymentForMe) => {
      if (!organization.hasData()) {
        console.error("Unable to do checkout due to missing organization data");
        avelaToast.genericError();
        return;
      }
      setSelectedPaymentId(payment.id);
      let redirectedToCheckout = false;
      try {
        const resp = await FSR.checkout({
          throwOnError: false,
          body: {
            paymentId: payment.id,
            successUrl: `${window.location.origin}${Url.Parent.Payment.receipt(
              organization.data
            )}`,
            cancelUrl: `${window.location.origin}${Url.Parent.Payment.index(
              organization.data
            )}`,
          },
        });
        const status = resp.response.status;
        if (!resp.response.ok) {
          console.error(resp.error);

          if (status === 409 && isNonEmptyString(resp.error?.error.message)) {
            avelaToast.error({
              title: resp.error.error.message,
              description: "",
            });
          } else {
            avelaToast.genericError();
          }
          return;
        }

        const checkoutUrl = resp.data?.data.checkoutUrl;
        if (isNullOrUndefined(checkoutUrl)) {
          console.error("Empty checkout url");
          avelaToast.genericError();
          return;
        }

        window.location.assign(checkoutUrl);
        redirectedToCheckout = true;
      } catch (err) {
        console.error(err);
        avelaToast.genericError();
      } finally {
        if (!redirectedToCheckout) {
          // keep Pay button state as is (disabled) when redirecting to checkout URL
          void refetch();
          setSelectedPaymentId(null);
        }
      }
    },
    [avelaToast, organization, refetch]
  );

  const paymentForMeRD = React.useMemo(
    () =>
      remoteData.map((data) => {
        const pending: FSR.PaymentForMe[] = [];
        const history: FSR.PaymentForMe[] = [];
        for (const payment of data.data) {
          if (payment.paymentStatus === "pending") {
            pending.push(payment);
          }
          if (payment.paymentStatus === "paid") {
            history.push(payment);
          }
        }
        return { pending, history };
      }),
    [remoteData]
  );

  return (
    <ParentFormsLayoutOuter heading="Payment">
      <ParentFormsLayoutInner>
        <RemoteDataView
          remoteData={paymentForMeRD}
          error={() => <GenericError />}
          loading={<Loading />}
          reloading={null}
        >
          {({ pending, history }) => {
            if (pending.length <= 0 && history.length <= 0) {
              return (
                <EmptyState
                  grow="1"
                  justifyContent="center"
                  description="Nothing’s been requested or paid for yet"
                  heading="No payments to see here"
                  Svg={EmptyWalletSvg}
                />
              );
            }
            return (
              <Flex direction="column" gap="8">
                {pending.length > 0 ? (
                  <Flex direction="column" gap="3">
                    <Heading
                      as="h2"
                      fontSize="sm"
                      fontWeight="semibold"
                      textTransform="uppercase"
                    >
                      Pending
                    </Heading>
                    {pending.map((payment) => (
                      <PendingPaymentCard
                        payment={payment}
                        key={payment.id}
                        payHandler={checkout}
                        buttonProps={{
                          isDisabled: selectedPaymentId !== null,
                          isLoading: selectedPaymentId === payment.id,
                        }}
                        refetch={refetch}
                      />
                    ))}
                  </Flex>
                ) : null}
                {history.length > 0 ? (
                  <Flex direction="column" gap="3">
                    <Heading
                      as="h2"
                      fontSize="sm"
                      fontWeight="semibold"
                      textTransform="uppercase"
                    >
                      History
                    </Heading>
                    {history.map((payment) => (
                      <PaidPaymentCard payment={payment} key={payment.id} />
                    ))}
                  </Flex>
                ) : null}
              </Flex>
            );
          }}
        </RemoteDataView>
      </ParentFormsLayoutInner>
    </ParentFormsLayoutOuter>
  );
};

const Loading = () => {
  return (
    <Flex direction="column" gap="2">
      <Skeleton width="150px" height="1.5rem" marginBottom="3" />
      <Skeleton width="100%" height="5rem" />
      <Skeleton width="100%" height="5rem" />
      <Skeleton width="150px" height="1.5rem" marginBottom="3" marginTop="8" />
      <Skeleton width="100%" height="5rem" />
    </Flex>
  );
};
